/**
 * usePlanetDragState - Event-driven state machine for planet drag interaction
 *
 * Manages the full drag lifecycle:
 * idle → loading → ready → dragging → releasing → idle
 *
 * Listens to events:
 * - planet.drag.gesture.start → starts loading buffer
 * - planet.drag.navigate → moves by degree offset
 * - planet.drag.end → releases/confirms selection
 * - planet.drag.cancel → cancels operation
 *
 * Emits events:
 * - planet.drag.start → when loading begins
 * - planet.drag.progress → loading progress updates
 * - planet.drag.ready → buffer loaded, ready for navigation
 * - planet.drag.move → planet position updated
 * - planet.drag.navigation.update → time/index changed
 */

import { devLog, devError } from '../utils/devLog';
import { useReducer, useCallback, useRef, useEffect } from 'react';
import { motionBufferService } from '../services/MotionBufferService';
import { globalEventStream, createEvent } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import type { BaseEvent, EventSubscription } from '../components/common/AstrologyChart/universal_architecture/types/EventTypes';
import type {
  DragState,
  DragAction,
  DegreeMotionBuffer,
  AllPlanetsSnapshot,
  InterpolatedPosition,
  DragUndoState,
} from '../types/MotionTypes';
import { getActiveMotion } from '../components/common/AstrologyChart/shared/motionState';

const DEBUG = true; // Enable debug logging

// Buffer is always 721 points (360 * 2 + 1), center is always at index 360
const CENTER_INDEX = 360;
const BUFFER_SIZE = 721;

// Track charts that have already done their first drag in this session
// Used to send forceRefresh only on first drag after page load or chart open
const chartsDragInitialized = new Set<string>();

/**
 * Parse ISO timestamp string to Date with local-as-UTC convention
 * This matches how parseChartDateTime creates Date objects
 * Input: "2025-12-31T04:25:53.220516+02:00" → Date.UTC(2025, 11, 31, 4, 25, 53)
 */
function parseISOToLocalAsUTC(isoString: string): Date {
  const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  if (!match) {
    return new Date(isoString); // Fallback
  }
  const [, year, month, day, hours, minutes, seconds] = match;
  return new Date(Date.UTC(
    parseInt(year), parseInt(month) - 1, parseInt(day),
    parseInt(hours), parseInt(minutes), parseInt(seconds)
  ));
}

// =============================================================================
// Reducer
// =============================================================================

function dragReducer(state: DragState, action: DragAction): DragState {
  if (DEBUG && action.type !== 'DRAG_MOVE' && action.type !== 'UPDATE_PROGRESS') {
    devLog(`[PlanetDrag:State] 🔄 ${state.type} → ${action.type}`);
  }

  switch (action.type) {
    case 'START_LOADING':
      // Allow loading from idle (initial) or dragging (step change)
      if (state.type !== 'idle' && state.type !== 'dragging') return state;
      return { type: 'loading', planet: action.planet, progress: 0 };

    case 'UPDATE_PROGRESS':
      if (state.type !== 'loading') return state;
      return { ...state, progress: Math.min(100, Math.max(0, action.progress)) };

    case 'BUFFER_LOADED':
      if (state.type !== 'loading') return state;
      if (DEBUG) devLog(`[PlanetDrag:State] ✅ Buffer loaded: ${action.buffer.data.length} points`);
      return {
        type: 'ready',
        planet: state.planet,
        buffer: action.buffer,
      };

    case 'START_DRAG':
      if (state.type !== 'ready') return state;
      return {
        type: 'dragging',
        planet: state.planet,
        buffer: state.buffer,
        currentIndex: state.buffer.centerIndex,
      };

    case 'DRAG_MOVE':
      if (state.type !== 'dragging') return state;
      const clampedIndex = Math.max(0, Math.min(action.index, state.buffer.data.length - 1));
      return { ...state, currentIndex: clampedIndex };

    case 'DRAG_RELEASE':
      if (state.type !== 'dragging') return state;
      if (DEBUG) devLog(`[PlanetDrag:State] 🎯 Release at ${action.selectedTime.toISOString()}`);
      return {
        type: 'releasing',
        planet: state.planet,
        selectedTime: action.selectedTime,
      };

    case 'CANCEL':
    case 'COMPLETE':
      return { type: 'idle' };

    default:
      return state;
  }
}

// =============================================================================
// Hook Interface
// =============================================================================

interface ChartLocation {
  lat: number;
  lon: number;
  timezoneOffset: number;
  chartDateTime: Date;  // Chart's date/time (not current browser time)
}

interface UsePlanetDragStateResult {
  // State
  state: DragState;
  undoState: DragUndoState | null;

  // Current data (when dragging)
  currentPosition: InterpolatedPosition | null;
  allPlanetsSnapshot: AllPlanetsSnapshot | null;

  // Helpers
  isLoading: boolean;
  isDragging: boolean;
  canUndo: boolean;

  // Manual trigger (for undo)
  undo: () => void;
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function usePlanetDragState(
  chartId: string,
  location: ChartLocation
): UsePlanetDragStateResult {
  const [state, dispatch] = useReducer(dragReducer, { type: 'idle' });
  const undoStateRef = useRef<DragUndoState | null>(null);
  const originalTimeRef = useRef<Date | null>(null);

  // Refs for current state (to avoid stale closures in event handlers)
  const stateRef = useRef(state);
  const locationRef = useRef(location);
  stateRef.current = state;
  locationRef.current = location;

  // Ref for buffer data (needed for navigation)
  const bufferRef = useRef<DegreeMotionBuffer | null>(null);
  const currentIndexRef = useRef<number>(CENTER_INDEX);  // Always start at center (360)
  const planetRef = useRef<string>('');
  const stepDegreesRef = useRef<number>(1.0);  // Current step size in degrees
  const abortControllerRef = useRef<AbortController | null>(null);  // For cancelling progressive loading
  const originalLongitudeRef = useRef<number | null>(null);  // Original planet longitude when drag started
  const baseOffsetDegreesRef = useRef<number>(0);  // Accumulated degree offset (preserved across step changes)

  // ===========================================================================
  // Effect: Subscribe to events
  // ===========================================================================

  useEffect(() => {
    if (DEBUG) devLog(`[PlanetDrag:State] 🔧 Subscribing to events for chart ${chartId}`);

    // -------------------------------------------------------------------------
    // Handler: Gesture Start → Start Loading
    // -------------------------------------------------------------------------
    const handleGestureStart = async (event: BaseEvent) => {
      if (event.metadata?.chartId !== chartId) return;
      if (stateRef.current.type !== 'idle') {
        if (DEBUG) devLog(`[PlanetDrag:State] ⚠️ Ignoring gesture, state is ${stateRef.current.type}`);
        return;
      }

      // Block if Time Motion is active
      const activeMotion = getActiveMotion(chartId);
      if (activeMotion?.type === 'time-motion') {
        if (DEBUG) devLog(`[PlanetDrag:State] ⚠️ Blocking gesture - Time Motion is active`);
        return;
      }

      const planet = event.metadata.planet as string;
      if (!planet) return;

      const loc = locationRef.current;
      const centerTime = loc.chartDateTime;  // Use chart's datetime, not current time!
      originalTimeRef.current = centerTime;
      planetRef.current = planet;

      if (DEBUG) devLog(`[PlanetDrag:State] 🚀 Starting load for ${planet}`, {
        lat: loc.lat,
        lon: loc.lon,
        timezoneOffset: loc.timezoneOffset,
        chartDateTime: centerTime?.toISOString(),
      });

      dispatch({ type: 'START_LOADING', planet });

      // Emit start event for UI
      globalEventStream.emit(createEvent<BaseEvent>(
        'planet.drag.start',
        'usePlanetDragState',
        { metadata: { chartId, planet } }
      ));

      // Use step from gesture event (from chart settings) or default to 1.0
      const stepFromSettings = (event.metadata?.stepDegrees as number) || 1.0;
      stepDegreesRef.current = stepFromSettings;

      if (DEBUG) devLog(`[PlanetDrag:State] Using step ${stepDegreesRef.current}° from settings`);

      // Create abort controller for this loading session
      abortControllerRef.current = new AbortController();

      // Force refresh only on first drag after page load or chart open
      const needsForceRefresh = !chartsDragInitialized.has(chartId);
      if (needsForceRefresh) {
        chartsDragInitialized.add(chartId);
        if (DEBUG) devLog(`[PlanetDrag:State] First drag for chart ${chartId}, forcing cache refresh`);
      }

      // Load degree-based buffer with progressive loading
      const buffer = await motionBufferService.loadDegreeBuffer(
        planet,
        centerTime,
        loc.lat,
        loc.lon,
        loc.timezoneOffset,
        360,  // 360 points per direction (actual range = 360 × stepDegrees)
        stepDegreesRef.current,  // Current step
        // onProgress - real progress from backend
        (progress) => {
          dispatch({ type: 'UPDATE_PROGRESS', progress });
          globalEventStream.emit(createEvent<BaseEvent>(
            'planet.drag.progress',
            'usePlanetDragState',
            { metadata: { chartId, planet, progress } }
          ));
        },
        // onInitialData - called when initial ±10 points are available
        (initialBuffer) => {
          if (DEBUG) devLog(`[PlanetDrag:State] 📦 Initial data: ${initialBuffer.data.length} points (progressive loading)`);
          bufferRef.current = initialBuffer;

          // Find actual center using universal method (handles progressive loading)
          const actualCenterIndex = motionBufferService.findActualCenterIndex(initialBuffer);
          currentIndexRef.current = actualCenterIndex;

          // Transition state machine to dragging state immediately
          dispatch({ type: 'BUFFER_LOADED', buffer: initialBuffer });
          dispatch({ type: 'START_DRAG' });

          const centerPoint = motionBufferService.getPointAtIndex(initialBuffer, actualCenterIndex);

          // Save undo state and ORIGINAL longitude (first load only)
          const snapshot = motionBufferService.getAllPlanetsAtCenter(initialBuffer);
          const planetPosition = snapshot?.planets[planet];

          // Save original longitude and reset base offset - this is the position when drag started
          originalLongitudeRef.current = planetPosition?.longitude ?? null;
          baseOffsetDegreesRef.current = 0;  // Start from zero offset
          if (DEBUG) devLog(`[PlanetDrag:State] 📍 Original longitude saved: ${originalLongitudeRef.current}°`);

          if (snapshot) {
            undoStateRef.current = {
              originalTime: centerTime,
              originalPositions: snapshot.planets,
              canUndo: true,
            };
          }

          if (centerPoint && snapshot) {
            // Update currentIndex to actual center in current data
            currentIndexRef.current = actualCenterIndex;

            globalEventStream.emit(createEvent<BaseEvent>(
              'planet.drag.ready',
              'usePlanetDragState',
              {
                metadata: {
                  chartId,
                  planet,
                  bufferSize: initialBuffer.data.length,
                  centerIndex: actualCenterIndex,
                  timestamp: centerPoint.t,
                  stepDegrees: stepDegreesRef.current,
                  chartTimezoneOffset: loc.timezoneOffset,
                  apiUtcOffset: snapshot.utcOffset,
                  originalLongitude: originalLongitudeRef.current,  // For Apply button
                  planetPosition: planetPosition ? {
                    longitude: planetPosition.longitude,
                    rashiIndex: planetPosition.rashiIndex,
                    degreesInRashi: planetPosition.degreesInRashi,
                  } : null,
                  canGoBack: actualCenterIndex > 0,
                  canGoForward: actualCenterIndex < initialBuffer.data.length - 1,
                  isPartial: true,  // Flag for UI to show loading indicator
                  progress: initialBuffer.progress,
                  degreeOffset: baseOffsetDegreesRef.current,  // Total offset from start (0 initially)
                },
              }
            ));
          }
        },
        abortControllerRef.current.signal,
        // onBufferUpdate - called when buffer expands with new data
        (updatedBuffer) => {
          if (DEBUG) devLog(`[PlanetDrag:State] 📥 Buffer update: ${updatedBuffer.data.length} points, complete: ${updatedBuffer.isComplete}`);

          // Save current position's degree offset (point.d) before buffer update
          const oldBuffer = bufferRef.current;
          const currentPoint = oldBuffer?.data[currentIndexRef.current];
          const currentDegreeOffset = currentPoint?.d ?? 0;

          bufferRef.current = updatedBuffer;

          // Find the same degree offset in updated buffer (data is sorted by d)
          let newIndex = updatedBuffer.data.findIndex(p => p.d === currentDegreeOffset);
          if (newIndex < 0) {
            // Fallback: calculate from centerIndex
            newIndex = updatedBuffer.centerIndex + currentDegreeOffset;
          }
          newIndex = Math.max(0, Math.min(newIndex, updatedBuffer.data.length - 1));
          currentIndexRef.current = newIndex;

          if (DEBUG) devLog(`[PlanetDrag:State] 📥 Preserved position: d=${currentDegreeOffset}, newIndex=${newIndex}`);

          // Emit navigation update with new bounds
          const canGoBack = newIndex > 0;
          const canGoForward = newIndex < updatedBuffer.data.length - 1;
          const point = updatedBuffer.data[newIndex];

          if (point) {
            globalEventStream.emit(createEvent<BaseEvent>(
              'planet.drag.navigation.update',
              'usePlanetDragState',
              {
                metadata: {
                  chartId,
                  newIndex,
                  centerIndex: updatedBuffer.centerIndex,  // Updated centerIndex for BufferIndicator
                  degreeOffset: baseOffsetDegreesRef.current + point.d,  // Total offset from start
                  timestamp: point.t,
                  planet,
                  planetPosition: null, // Keep current position
                  canGoBack,
                  canGoForward,
                  isPartial: !updatedBuffer.isComplete,
                  bufferSize: updatedBuffer.data.length,
                },
              }
            ));
          }
        },
        needsForceRefresh  // forceRefresh only on first drag after page load/chart open
      );

      if (buffer) {
        // Check if we're already in dragging state (progressive loading completed)
        // In that case, onInitialData and onBufferUpdate already handled state updates
        // We should NOT reset to center - user might have navigated elsewhere
        // Note: Using string comparison to avoid TypeScript type narrowing issues
        // (state can change during await via callbacks)
        const currentStateType = stateRef.current.type as string;
        if (currentStateType === 'dragging') {
          const currentPoint = bufferRef.current?.data[currentIndexRef.current];
          if (DEBUG) devLog(`[PlanetDrag:State] ✅ Progressive loading complete, keeping current position (d=${currentPoint?.d ?? 0})`);
          // Just update the buffer ref with final complete buffer
          bufferRef.current = buffer;
          return;
        }

        // This code path runs only for immediate complete response (no progressive loading)
        bufferRef.current = buffer;
        // Use universal method for consistency
        const actualCenterIndex = motionBufferService.findActualCenterIndex(buffer);
        currentIndexRef.current = actualCenterIndex;

        dispatch({ type: 'BUFFER_LOADED', buffer });

        // Save undo state using universal method
        const snapshot = motionBufferService.getAllPlanetsAtCenter(buffer);
        if (!snapshot) {
          devError(`[PlanetDrag:State] ❌ No center point in buffer, size: ${buffer.data.length}`);
          dispatch({ type: 'CANCEL' });
          return;
        }
        undoStateRef.current = {
          originalTime: centerTime,
          originalPositions: snapshot.planets,
          canUndo: true,
        };

        // Get initial timestamp from center point
        const centerPoint = motionBufferService.getCenterPoint(buffer);
        const initialTimestamp = centerPoint!.t;

        // Get initial planet position for display
        const initialPlanetPosition = snapshot.planets[planet];

        // Save original longitude and reset base offset - this is the position when drag started (cache hit path)
        originalLongitudeRef.current = initialPlanetPosition?.longitude ?? null;
        baseOffsetDegreesRef.current = 0;  // Start from zero offset
        if (DEBUG) devLog(`[PlanetDrag:State] 📍 Original longitude saved (cache): ${originalLongitudeRef.current}°`);

        // Emit ready event with initial time and position
        globalEventStream.emit(createEvent<BaseEvent>(
          'planet.drag.ready',
          'usePlanetDragState',
          {
            metadata: {
              chartId,
              planet,
              bufferSize: buffer.data.length,
              centerIndex: buffer.centerIndex,
              timestamp: initialTimestamp,
              stepDegrees: stepDegreesRef.current,
              // Timezone info for display correction
              chartTimezoneOffset: loc.timezoneOffset,
              apiUtcOffset: snapshot.utcOffset,
              // Initial planet position for display
              planetPosition: initialPlanetPosition ? {
                longitude: initialPlanetPosition.longitude,
                rashiIndex: initialPlanetPosition.rashiIndex,
                degreesInRashi: initialPlanetPosition.degreesInRashi,
              } : null,
              // Bounds info for button state
              canGoBack: buffer.centerIndex > 0,
              canGoForward: buffer.centerIndex < buffer.data.length - 1,
            },
          }
        ));

        // Auto-start drag mode
        dispatch({ type: 'START_DRAG' });

        if (DEBUG) devLog(`[PlanetDrag:State] ✅ Buffer ready, ${buffer.data.length} points, auto-started drag`);
      } else {
        dispatch({ type: 'CANCEL' });
        globalEventStream.emit(createEvent<BaseEvent>(
          'planet.drag.cancel',
          'usePlanetDragState',
          { metadata: { chartId, planet, reason: 'buffer_load_failed' } }
        ));
      }
    };

    // -------------------------------------------------------------------------
    // Handler: Navigate → Move by index delta (simple: +1/-1 or +10/-10)
    // -------------------------------------------------------------------------
    const handleNavigate = (event: BaseEvent) => {
      if (event.metadata?.chartId !== chartId) return;

      const buffer = bufferRef.current;
      const planet = planetRef.current;

      if (!buffer || !planet) {
        if (DEBUG) devLog(`[PlanetDrag:State] ⚠️ Navigate: no buffer/planet`);
        return;
      }

      if (stateRef.current.type !== 'dragging') {
        if (DEBUG) devLog(`[PlanetDrag:State] ⚠️ Navigate: not in dragging state`);
        return;
      }

      // Simple index-based navigation: +1/-1 (normal), +10/-10 (with Shift)
      const indexDelta = event.metadata.indexDelta as number;
      if (indexDelta === undefined || indexDelta === 0) return;

      // Calculate new index with bounds check
      let newIndex = currentIndexRef.current + indexDelta;
      newIndex = Math.max(0, Math.min(newIndex, buffer.data.length - 1));

      // Skip if no movement (at boundary)
      if (newIndex === currentIndexRef.current) {
        if (DEBUG) devLog(`[PlanetDrag:State] ⚠️ Navigate: at boundary`);
        return;
      }

      // Update current index
      currentIndexRef.current = newIndex;
      dispatch({ type: 'DRAG_MOVE', index: newIndex });

      // Get data directly from buffer
      const point = buffer.data[newIndex];
      const allPlanets = motionBufferService.getAllPlanetsAtDegreeIndex(buffer, newIndex);
      const planetPosition = allPlanets.planets[planet];

      if (DEBUG) devLog(`[PlanetDrag:State] 🧭 Navigate: index ${newIndex}, d=${point.d}`);

      // Emit move event for renderer
      globalEventStream.emit(createEvent<BaseEvent>(
        'planet.drag.move',
        'usePlanetDragState',
        {
          metadata: {
            chartId,
            planet,
            position: { timestamp: allPlanets.timestamp },
            allPlanets,
          },
        }
      ));

      // Emit navigation update for overlay
      globalEventStream.emit(createEvent<BaseEvent>(
        'planet.drag.navigation.update',
        'usePlanetDragState',
        {
          metadata: {
            chartId,
            newIndex,
            degreeOffset: baseOffsetDegreesRef.current + point.d,  // Total offset from start
            timestamp: allPlanets.timestampStr,
            planet,
            chartTimezoneOffset: locationRef.current.timezoneOffset,
            apiUtcOffset: allPlanets.utcOffset,
            originalLongitude: originalLongitudeRef.current,  // For Apply button
            planetPosition: planetPosition ? {
              longitude: planetPosition.longitude,
              rashiIndex: planetPosition.rashiIndex,
              degreesInRashi: planetPosition.degreesInRashi,
            } : null,
            canGoBack: newIndex > 0,
            canGoForward: newIndex < buffer.data.length - 1,
          },
        }
      ));
    };

    // -------------------------------------------------------------------------
    // Handler: End → Confirm selection
    // -------------------------------------------------------------------------
    const handleEnd = (event: BaseEvent) => {
      if (event.metadata?.chartId !== chartId) return;

      // If emitted by overlay (Apply button), we process it
      // If emitted by self (from undo), ignore to avoid loop
      if (event.source === 'usePlanetDragState' && !event.metadata?.isUndo) return;

      const buffer = bufferRef.current;
      if (!buffer) return;

      const selectedTimeStr = event.metadata?.selectedTime as string;
      if (!selectedTimeStr) return;

      const selectedTime = new Date(selectedTimeStr);

      if (DEBUG) devLog(`[PlanetDrag:State] 🎯 End: ${selectedTime.toISOString()}`);

      dispatch({ type: 'DRAG_RELEASE', selectedTime });
      dispatch({ type: 'COMPLETE' });

      // Emit internal event for Jataka recalculation
      globalEventStream.emit(createEvent<BaseEvent>(
        'planet.drag.confirmed',
        'usePlanetDragState',
        {
          metadata: {
            chartId,
            planet: planetRef.current,
            selectedTime: selectedTime.toISOString(),
          },
        }
      ));

      // Reset refs
      bufferRef.current = null;
      currentIndexRef.current = CENTER_INDEX;
      originalLongitudeRef.current = null;
      baseOffsetDegreesRef.current = 0;
      planetRef.current = '';
    };

    // -------------------------------------------------------------------------
    // Handler: Cancel
    // -------------------------------------------------------------------------
    const handleCancel = (event: BaseEvent) => {
      if (event.metadata?.chartId !== chartId) return;
      // Ignore self-emitted cancels
      if (event.source === 'usePlanetDragState') return;

      if (DEBUG) devLog(`[PlanetDrag:State] ❌ Cancel`);

      // Abort any ongoing progressive loading
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Restore original positions before resetting
      const buffer = bufferRef.current;
      const originalPositions = buffer ? motionBufferService.getAllPlanetsAtCenter(buffer) : null;
      if (originalPositions) {
        // Emit move event to restore original positions
        globalEventStream.emit(createEvent<BaseEvent>(
          'planet.drag.move',
          'usePlanetDragState',
          {
            metadata: {
              chartId,
              planet: planetRef.current,
              allPlanets: originalPositions,
            },
          }
        ));
      }

      dispatch({ type: 'CANCEL' });

      // Reset refs
      bufferRef.current = null;
      currentIndexRef.current = CENTER_INDEX;
      originalLongitudeRef.current = null;
      baseOffsetDegreesRef.current = 0;
      planetRef.current = '';
    };

    // -------------------------------------------------------------------------
    // Helper: Reload buffer with new step (shared by step change handlers)
    // Step change: current time becomes new center, index resets to CENTER_INDEX
    // -------------------------------------------------------------------------
    const reloadBufferWithStep = async (planet: string, newStep: number, currentTime: Date) => {
      if (DEBUG) devLog(`[PlanetDrag:State] 🔄 Reloading buffer: ${stepDegreesRef.current}° → ${newStep}°`);

      stepDegreesRef.current = newStep;
      dispatch({ type: 'START_LOADING', planet });

      // Emit start event so overlay shows loading state
      globalEventStream.emit(createEvent<BaseEvent>(
        'planet.drag.start',
        'usePlanetDragState:reloadBufferWithStep',
        { metadata: { chartId, planet } }
      ));

      const loc = locationRef.current;

      // Abort any previous loading
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const buffer = await motionBufferService.loadDegreeBuffer(
        planet,
        currentTime,  // Current position becomes new center
        loc.lat,
        loc.lon,
        loc.timezoneOffset,
        360,
        newStep,
        // onProgress
        (progress) => {
          dispatch({ type: 'UPDATE_PROGRESS', progress });
          globalEventStream.emit(createEvent<BaseEvent>(
            'planet.drag.progress',
            'usePlanetDragState',
            { metadata: { chartId, planet, progress } }
          ));
        },
        // onInitialData - show navigation as soon as initial data arrives
        (initialBuffer) => {
          if (DEBUG) devLog(`[PlanetDrag:State] 📦 Step change initial data: ${initialBuffer.data.length} points`);

          bufferRef.current = initialBuffer;
          // Use universal method for consistency
          const actualCenterIndex = motionBufferService.findActualCenterIndex(initialBuffer);
          currentIndexRef.current = actualCenterIndex;

          dispatch({ type: 'BUFFER_LOADED', buffer: initialBuffer });
          dispatch({ type: 'START_DRAG' });

          const centerPoint = motionBufferService.getCenterPoint(initialBuffer);
          const snapshot = motionBufferService.getAllPlanetsAtCenter(initialBuffer);
          const planetPosition = snapshot?.planets[planet];

          globalEventStream.emit(createEvent<BaseEvent>(
            'planet.drag.ready',
            'usePlanetDragState',
            {
              metadata: {
                chartId,
                planet,
                bufferSize: initialBuffer.data.length,
                centerIndex: actualCenterIndex,
                timestamp: centerPoint?.t,
                stepDegrees: newStep,
                chartTimezoneOffset: loc.timezoneOffset,
                apiUtcOffset: snapshot?.utcOffset || 0,
                isPartial: true,
                progress: 2,
                degreeOffset: baseOffsetDegreesRef.current,  // Preserved total offset from start
                originalLongitude: originalLongitudeRef.current,  // Keep original for Apply button
                planetPosition: planetPosition ? {
                  longitude: planetPosition.longitude,
                  rashiIndex: planetPosition.rashiIndex,
                  degreesInRashi: planetPosition.degreesInRashi,
                } : null,
                canGoBack: actualCenterIndex > 0,
                canGoForward: actualCenterIndex < initialBuffer.data.length - 1,
              },
            }
          ));
        },
        abortControllerRef.current.signal
      );

      // Buffer update after full load
      if (buffer) {
        // Check if we're already in dragging state (progressive loading completed)
        // If still in loading state, we need to transition to dragging (cache hit path)
        const currentStateType = stateRef.current.type as string;
        const isCacheHit = currentStateType !== 'dragging';

        if (isCacheHit) {
          // Cache hit: onInitialData was never called, transition state now
          dispatch({ type: 'BUFFER_LOADED', buffer });
          dispatch({ type: 'START_DRAG' });
          if (DEBUG) devLog(`[PlanetDrag:State] 🔄 Step change cache hit, transitioning to dragging`);
        }

        // For cache hit: user stays at center (d=0) - new buffer IS centered on current position
        // For progressive: user might have navigated during loading, get offset from PARTIAL buffer
        const navigationOffset = isCacheHit ? 0 : (bufferRef.current?.data[currentIndexRef.current]?.d || 0);

        bufferRef.current = buffer;

        // Use actual center for cache hit, or calculated position for progressive
        const actualCenterIndex = motionBufferService.findActualCenterIndex(buffer);
        const targetIndex = isCacheHit
          ? actualCenterIndex
          : Math.max(0, Math.min(buffer.centerIndex + Math.round(navigationOffset / newStep), buffer.data.length - 1));
        currentIndexRef.current = targetIndex;

        const currentPoint = buffer.data[targetIndex];
        const snapshot = motionBufferService.getAllPlanetsAtDegreeIndex(buffer, targetIndex);
        const planetPosition = snapshot.planets[planet];

        // For cache hit: currentPoint.d should be 0 at center, totalOffset = baseOffset
        // For progressive: currentPoint.d is navigation during loading
        const totalOffset = baseOffsetDegreesRef.current + (isCacheHit ? 0 : (currentPoint?.d || 0));

        globalEventStream.emit(createEvent<BaseEvent>(
          'planet.drag.ready',
          'usePlanetDragState',
          {
            metadata: {
              chartId,
              planet,
              bufferSize: buffer.data.length,
              centerIndex: buffer.centerIndex,
              timestamp: currentPoint?.t,
              stepDegrees: newStep,
              chartTimezoneOffset: loc.timezoneOffset,
              apiUtcOffset: snapshot.utcOffset || 0,
              isPartial: !buffer.isComplete,
              progress: buffer.isComplete ? 100 : 50,
              degreeOffset: totalOffset,  // Total offset including navigation during loading
              originalLongitude: originalLongitudeRef.current,  // Keep original for Apply button
              planetPosition: planetPosition ? {
                longitude: planetPosition.longitude,
                rashiIndex: planetPosition.rashiIndex,
                degreesInRashi: planetPosition.degreesInRashi,
              } : null,
              canGoBack: targetIndex > 0,
              canGoForward: targetIndex < buffer.data.length - 1,
            },
          }
        ));

        if (DEBUG) devLog(`[PlanetDrag:State] ✅ Buffer reloaded: ${buffer.data.length} points, offset: ${totalOffset}°`);
      }
    };

    // -------------------------------------------------------------------------
    // Handler: Step Change → Reload buffer with new step
    // -------------------------------------------------------------------------
    const handleStepChange = async (event: BaseEvent) => {
      if (event.metadata?.chartId !== chartId) return;
      if (stateRef.current.type !== 'dragging') return;

      const newStep = event.metadata.newStep as number;
      const currentTimeStr = event.metadata.currentTime as string;
      const planet = planetRef.current;

      if (!newStep || !currentTimeStr || !planet) return;

      await reloadBufferWithStep(planet, newStep, parseISOToLocalAsUTC(currentTimeStr));
    };

    // -------------------------------------------------------------------------
    // Handler: Movement Settings Changed → Reload buffer with new step
    // -------------------------------------------------------------------------
    const handleMovementStepChanged = async (event: BaseEvent) => {
      if (event.metadata?.chartId !== chartId) return;
      if (stateRef.current.type !== 'dragging') return;

      const planet = event.metadata?.planet as string;
      const newStep = event.metadata?.stepDegrees as number;

      if (!planet || planet !== planetRef.current || !newStep) return;

      const buffer = bufferRef.current;
      if (!buffer) return;

      const currentPoint = buffer.data[currentIndexRef.current];
      if (!currentPoint) return;

      // Save current total offset before reload (baseOffset + current point's offset)
      baseOffsetDegreesRef.current = baseOffsetDegreesRef.current + (currentPoint.d || 0);
      if (DEBUG) devLog(`[PlanetDrag:State] 📍 Saving offset before step change: ${baseOffsetDegreesRef.current}°`);

      await reloadBufferWithStep(planet, newStep, parseISOToLocalAsUTC(currentPoint.t));
    };

    // Subscribe to events
    const subscriptions: EventSubscription[] = [
      globalEventStream.subscribe('planet.drag.gesture.start', handleGestureStart),
      globalEventStream.subscribe('planet.drag.navigate', handleNavigate),
      globalEventStream.subscribe('planet.drag.end', handleEnd),
      globalEventStream.subscribe('planet.drag.cancel', handleCancel),
      globalEventStream.subscribe('planet.drag.step.change', handleStepChange),
      globalEventStream.subscribe('planet.movement.step.changed', handleMovementStepChanged),
    ];

    // Cleanup: remove chart from init tracker when chart is unloaded
    const unloadSub = globalEventStream.subscribe('chart.unloaded', (event: any) => {
      const unloadedChartId = event?.data?.chartId || event?.chartId;
      if (unloadedChartId) {
        chartsDragInitialized.delete(unloadedChartId);
        if (DEBUG) devLog(`[PlanetDrag:State] 🧹 Removed ${unloadedChartId} from drag init tracker`);
      }
    });

    return () => {
      if (DEBUG) devLog(`[PlanetDrag:State] 🧹 Unsubscribing for chart ${chartId}`);
      subscriptions.forEach(sub => sub.unsubscribe());
      unloadSub.unsubscribe();
    };
  }, [chartId]);

  // ===========================================================================
  // Undo action (manual trigger)
  // ===========================================================================

  const undo = useCallback(() => {
    if (!undoStateRef.current?.canUndo) return;

    // Emit event to restore original time
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.end',
      'usePlanetDragState',
      {
        metadata: {
          chartId,
          planet: '',
          selectedTime: undoStateRef.current.originalTime.toISOString(),
          isUndo: true,
        },
      }
    ));

    undoStateRef.current = {
      ...undoStateRef.current,
      canUndo: false,
    };
  }, [chartId]);

  // ===========================================================================
  // Computed Values
  // ===========================================================================

  // Get all planet positions at current index
  const allPlanetsSnapshot = state.type === 'dragging'
    ? motionBufferService.getAllPlanetsAtDegreeIndex(state.buffer, state.currentIndex)
    : null;

  // Get current planet position from snapshot
  const currentPosition = (state.type === 'dragging' && allPlanetsSnapshot)
    ? {
        ...allPlanetsSnapshot.planets[state.planet],
        timestamp: allPlanetsSnapshot.timestamp,
      } as InterpolatedPosition
    : null;

  // ===========================================================================
  // Return
  // ===========================================================================

  return {
    state,
    undoState: undoStateRef.current,

    currentPosition,
    allPlanetsSnapshot,

    isLoading: state.type === 'loading',
    isDragging: state.type === 'dragging',
    canUndo: undoStateRef.current?.canUndo ?? false,

    undo,
  };
}

// =============================================================================
// Export reducer for testing
// =============================================================================

export { dragReducer };
